import type {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type {
  SupportTicketThreadContext,
  SupportTicketThreadResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  getSupportTicketIdScope,
  type SupportTicketCollectionFilter,
} from "./support-ticket-collection-scope";

/** Shape hooks return: entity fields + per-ticket link fields from the join. */
export type SupportTicketThreadCollectionItem = ChannelThread & {
  ticketContext?: SupportTicketThreadContext;
  messageCount?: number;
};

export interface SupportTicketThreadLink {
  threadId: ChannelThreadId;
  ticketContext?: SupportTicketThreadContext;
  messageCount?: number;
}

export interface SupportTicketMessageLink {
  messageId: MessageId;
}

export interface SupportTicketTranscriptApi {
  getThreads(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketThreadResponse[] }>;
  getMessages(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly Message[] }>;
  getTimelineEvents(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketTimelineEventResponse[] }>;
  getThreadMessages(
    supportTicketId: SupportTicketId,
    threadId: ChannelThreadId,
  ): Promise<{ items: readonly Message[] }>;
}

export function getSupportTicketIdFilter(
  filters: readonly SupportTicketCollectionFilter[],
): SupportTicketId | null {
  return getSupportTicketIdScope(filters);
}

export interface SupportTicketThreadsFetchResult {
  entities: ChannelThread[];
  links: SupportTicketThreadLink[];
}

export interface SupportTicketMessagesFetchResult {
  entities: Message[];
  links: SupportTicketMessageLink[];
}

export async function fetchSupportTicketThreads(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadsFetchResult> {
  const result = await api.getThreads(supportTicketId);
  const entities: ChannelThread[] = [];
  const links: SupportTicketThreadLink[] = [];
  for (const { thread, ticketContext, messageCount } of result.items) {
    entities.push(thread);
    links.push({
      threadId: thread.id,
      ...(ticketContext ? { ticketContext } : {}),
      ...(messageCount !== undefined ? { messageCount } : {}),
    });
  }
  return { entities, links };
}

export async function fetchSupportTicketMessages(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketMessagesFetchResult> {
  const result = await api.getMessages(supportTicketId);
  const entities: Message[] = [...result.items];
  const links: SupportTicketMessageLink[] = entities.map((message) => ({
    messageId: message.id,
  }));
  return { entities, links };
}
